import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  GraduationCap, User, Phone, Mail, X, LogIn, CheckCircle, ArrowRight, Crown, Lock, Eye, EyeOff
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Numbers that require an extra access password (verified server-side)
const PROTECTED_NUMBERS = ["7876761066", "8076623513", "9113782617"];

// Generate a UNIQUE ID per tab/window (not per browser)
const getWindowId = () => {
  let windowId = sessionStorage.getItem('window_id');
  if (!windowId) {
    windowId = 'win_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('window_id', windowId);
  }
  return windowId;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Auth({ onLogin }) {
  const navigate = useNavigate();
  const [loginInput, setLoginInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Extra access password (for protected numbers)
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessPassword, setAccessPassword] = useState("");
  const [showAccessPwd, setShowAccessPwd] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null); // hold the validated input

  // Enrollment state
  const [enrollData, setEnrollData] = useState({ name: "", mobile: "", email: "" });
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [enrollDone, setEnrollDone] = useState(false);

  // Load Razorpay script on mount
  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch {} };
  }, []);

  // Login — mobile number or email
  const handleLogin = async (e) => {
    e.preventDefault();
    const input = loginInput.trim();
    const isEmail = input.includes('@');
    
    if (!isEmail && !/^\d{10}$/.test(input.replace(/[\s\-]/g, ''))) {
      toast.error("Enter a valid 10-digit mobile number or email address");
      return;
    }
    if (isEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)) {
      toast.error("Enter a valid email address");
      return;
    }

    const cleanMobile = input.replace(/[\s\-]/g, '');

    // Extra access password required for protected numbers
    if (PROTECTED_NUMBERS.includes(cleanMobile)) {
      setPendingLogin(input);
      setAccessPassword("");
      setShowAccessModal(true);
      return;
    }

    await doLogin(input);
  };

  const handleAccessPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-admin-password`, {
        mobile: pendingLogin,
        password: accessPassword,
      });
      setShowAccessModal(false);
      await doLogin(pendingLogin);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Incorrect access password");
      setLoading(false);
    }
  };

  const doLogin = async (input) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        mobile: input,
        device_id: getWindowId(),
        device_type: 'web'
      });
      const userData = response.data;
      localStorage.setItem('session_token', userData.session_token);
      localStorage.setItem('user', JSON.stringify(userData));

      if (userData.plan === 'premium') {
        toast.success("Welcome back! Premium access active.");
      } else {
        toast.success("Login successful!");
      }

      onLogin(userData);
      const returnTo = localStorage.getItem("returnTo");
      if (returnTo) {
        localStorage.removeItem("returnTo");
        navigate(returnTo);
      } else {
        navigate("/category");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // Enrollment (new student)
  const handleEnroll = async (e) => {
    e.preventDefault();
    const mob = enrollData.mobile.replace(/\D/g, "");
    if (!enrollData.name.trim() || enrollData.name.trim().length < 2) {
      toast.error("Enter your full name"); return;
    }
    if (!/^\d{10}$/.test(mob)) {
      toast.error("Enter a valid 10-digit mobile number"); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enrollData.email)) {
      toast.error("Enter a valid email address"); return;
    }
    if (!window.Razorpay) {
      toast.error("Payment gateway not loaded. Please refresh."); return;
    }

    setEnrollLoading(true);
    try {
      const { data } = await axios.post(`${API}/enrollment/initiate`, {
        name: enrollData.name.trim(),
        mobile: mob,
        email: enrollData.email.trim().toLowerCase(),
      });

      const options = {
        key: data.key,
        amount: data.amount,
        currency: data.currency,
        name: "Descriptive Test",
        description: "Premium Enrollment — Lifetime Access",
        order_id: data.order_id,
        handler: async (response) => {
          try {
            const verify = await axios.post(`${API}/enrollment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: data.user_id,
              is_new_user: data.is_new_user,
            });
            if (verify.data.success && verify.data.user) {
              const userData = verify.data.user;
              localStorage.setItem('user', JSON.stringify(userData));
              localStorage.setItem('session_token', userData.session_token);
              toast.success("Enrolled! Logging you in...");
              onLogin(userData);
              navigate('/category');
            } else {
              setEnrollDone(true);
              toast.success("Enrolled! You can now login with your mobile number.");
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed. Contact support.");
          }
          setEnrollLoading(false);
        },
        prefill: { name: data.name, email: data.email, contact: data.mobile },
        theme: { color: "#6366f1" },
        modal: { ondismiss: () => setEnrollLoading(false) },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg === "already_enrolled") {
        toast.info("Already enrolled! Just enter your mobile number or email to login.");
        setLoginInput(enrollData.mobile.replace(/\D/g, ""));
      } else {
        toast.error(msg || "Something went wrong. Please try again.");
      }
      setEnrollLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4 flex flex-col">
      <Helmet>
        <title>Descriptive Test - EMRS KVS NVS TGT PGT PRT Tier-2 Exam Preparation 2026</title>
        <meta name="description" content="India's best platform for EMRS, KVS, NVS TGT PGT PRT Tier-2 exam preparation. AI-powered descriptive test evaluation, MCQ practice, mock tests, NCERT study notes. Complete syllabus coverage for Hindi, English, Maths, Science, Physics, Chemistry, Biology, Commerce, History, Geography." />
        <meta name="keywords" content="EMRS exam preparation, KVS TGT PGT preparation, NVS tier 2 exam, EMRS descriptive test, KVS descriptive paper, EMRS TGT syllabus 2026, EMRS PGT syllabus 2026, KVS PRT exam, EMRS mock test, KVS mock test, NVS mock test, EMRS objective test, teacher recruitment exam 2026, CBSE pattern descriptive questions, TGT PGT MCQ practice, EMRS tier 2 preparation, KVS tier 2 exam, NVS teacher exam" />
        <meta property="og:title" content="Descriptive Test - EMRS KVS NVS Exam Preparation 2026" />
        <meta property="og:description" content="AI-powered descriptive test practice, MCQ preparation, mock tests for EMRS, KVS, NVS TGT PGT PRT Tier-2 exams. Complete CBSE syllabus coverage." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin + "/"} />
        <link rel="canonical" href={window.location.origin + "/"} />
      </Helmet>

      {/* Access Password Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-base">Access Required</h3>
                <p className="text-xs text-gray-500">Enter your access password to continue</p>
              </div>
            </div>
            <form onSubmit={handleAccessPasswordSubmit} className="space-y-4">
              <div className="relative">
                <Input
                  type={showAccessPwd ? "text" : "password"}
                  placeholder="Access password"
                  value={accessPassword}
                  onChange={e => setAccessPassword(e.target.value)}
                  className="pr-10"
                  autoFocus
                  data-testid="access-password-input"
                />
                <button type="button" onClick={() => setShowAccessPwd(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showAccessPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1"
                  onClick={() => { setShowAccessModal(false); setAccessPassword(""); }}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  disabled={loading} data-testid="access-password-submit">
                  {loading ? "Verifying..." : "Continue"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Background decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      <div className="flex-grow w-full relative z-10">
        {/* Header */}
        <div className="text-center space-y-4 px-4 mb-8">
          <div className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg">
            <GraduationCap className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Descriptive Test
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 leading-tight">
            EMRS KVS NVS
            <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Descriptive Paper Practice
            </span>
          </h1>
          <p className="text-base text-gray-600 max-w-xl mx-auto">
            Practice descriptive papers with instant CBSE Expert Team feedback
          </p>
        </div>

        {/* Two Cards */}
        <div className="w-full max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 px-4">

          {/* NEW STUDENT — Enrollment Card */}
          <Card className="shadow-2xl border-0 backdrop-blur-sm bg-white/95">
            <CardHeader className="space-y-1 pb-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
              <CardTitle className="text-xl font-bold flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-300" />
                New Student
              </CardTitle>
              <p className="text-indigo-100 text-sm">Enroll & get lifetime premium access @ ₹499</p>
            </CardHeader>
            <CardContent className="pt-6">
              {enrollDone ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">Enrollment Confirmed!</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    You can now login with your mobile number <strong>{enrollData.mobile}</strong>
                  </p>
                  <Button className="w-full" variant="outline" onClick={() => { setLoginInput(enrollData.mobile.replace(/\D/g, "")); setEnrollDone(false); }}>
                    Go to Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleEnroll} className="space-y-4" data-testid="enroll-form">
                  <div className="space-y-2">
                    <Label htmlFor="enroll-name">Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input id="enroll-name" data-testid="enroll-name-input" type="text" placeholder="Your full name" className="pl-10"
                        value={enrollData.name} onChange={(e) => setEnrollData({ ...enrollData, name: e.target.value })} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="enroll-mobile">Mobile Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input id="enroll-mobile" data-testid="enroll-mobile-input" type="tel" placeholder="9876543210" className="pl-10"
                        value={enrollData.mobile} maxLength={10} onChange={(e) => setEnrollData({ ...enrollData, mobile: e.target.value.replace(/\D/g, "") })} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="enroll-email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input id="enroll-email" data-testid="enroll-email-input" type="email" placeholder="you@example.com" className="pl-10"
                        value={enrollData.email} onChange={(e) => setEnrollData({ ...enrollData, email: e.target.value })} required />
                    </div>
                  </div>
                  <Button type="submit" disabled={enrollLoading} data-testid="enroll-submit-btn"
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold flex items-center justify-center gap-2">
                    {enrollLoading ? (
                      <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Processing...</>
                    ) : (
                      <><Crown className="w-4 h-4 text-amber-300" /> Enroll Now — ₹499 <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                  <p className="text-center text-xs text-gray-400">One-time payment · Lifetime access</p>
                </form>
              )}
            </CardContent>
          </Card>

          {/* EXISTING STUDENT — Login Card (Mobile Only) */}
          <Card className="shadow-2xl border-0 backdrop-blur-sm bg-white/95">
            <CardHeader className="space-y-1 pb-4 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-t-lg">
              <CardTitle className="text-xl font-bold flex items-center gap-2">
                <LogIn className="w-5 h-5" />
                Existing Student
              </CardTitle>
              <p className="text-green-100 text-sm">Enter your mobile number or email to login</p>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-input">Mobile Number or Email</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="login-input"
                      data-testid="login-mobile-input"
                      type="text"
                      placeholder="9876543210 or you@email.com"
                      className="pl-10 text-lg h-12"
                      value={loginInput}
                      onChange={(e) => setLoginInput(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-gray-400">No password needed — use your registered mobile or email</p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 h-12 text-lg"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? "Logging in..." : "Login"}
                </Button>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                  <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">or</span></div>
                </div>
                <Button type="button" variant="outline" className="w-full" onClick={handleGoogleLogin} data-testid="google-login-btn">
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                  <strong>1 device at a time:</strong> Your account can only be active on 1 device/browser. Logging in elsewhere will automatically log you out here.
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Links */}
        <div className="flex justify-center gap-6 mt-8 flex-wrap px-4">
          {[
            { label: "EMRS Syllabus", icon: "📚", href: "/emrs-syllabus" },
            { label: "KVS NVS Papers", icon: "📝", href: "/question-bank" },
            { label: "TGT PGT Practice", icon: "✏️", href: "/question-bank" },
          ].map((link) => (
            <Link key={link.label} to={link.href}
              className="flex items-center gap-2 bg-white/80 hover:bg-white border border-gray-200 hover:border-indigo-300 px-4 py-2 rounded-full text-sm text-gray-600 hover:text-indigo-600 transition-all shadow-sm">
              <span>{link.icon}</span>{link.label}
            </Link>
          ))}
        </div>

        {/* Admin Utility Links */}
        <div className="flex justify-center gap-4 mt-5 flex-wrap px-4">
          {[
            { label: "Notes", href: "/notes", testid: "auth-notes-btn" },
            { label: "LMS", href: "/lms-manager", testid: "auth-lms-btn" },
            { label: "Admin", href: "/admin", testid: "auth-admin-btn" },
            { label: "Question Bank", href: "/questionbank", testid: "auth-qb-btn" },
          ].map((link) => (
            <Link key={link.label} to={link.href} data-testid={link.testid}
              className="text-xs text-gray-400 hover:text-indigo-500 hover:underline transition-colors">
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
